import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, ArrowRight, Calendar as CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../i18n/I18nProvider";
import Reveal from "../components/Reveal";
import SEO from "../components/SEO";
import { getSiteOrigin } from "../siteOrigin";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";

export default function ContactPage() {
  const { t, lang } = useI18n();
  const SITE = getSiteOrigin();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    project_type: t.contact.project_options[0],
    budget: t.contact.budget_options[1],
    target_date: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", { ...form, locale: lang });
      setDone(true);
      toast.success(t.contact.success);
      setForm({ ...form, name: "", email: "", phone: "", company: "", target_date: "", message: "" });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="pt-40 lg:pt-48 pb-32 bg-[#050505] min-h-screen" data-testid="page-contact">
      <SEO
        title={lang === "fr"
          ? "Contactez Phaneroo Prod — Production Vidéo & Photo Moncton NB"
          : "Contact Phaneroo Prod — Video & Photo Production Moncton NB"}
        description={lang === "fr"
          ? "Contactez Phaneroo Prod à Moncton, NB pour votre projet vidéo ou photo. Tél : +1 (506) 871-6122. Devis gratuit sous 24h. Studio bilingue FR/EN, Canada Atlantique."
          : "Contact Phaneroo Prod in Moncton, NB for your video or photo project. Phone: +1 (506) 871-6122. Free quote within 24 hours. Bilingual FR/EN studio, Atlantic Canada."}
        path="/contact"
        lang={lang}
        schema={[{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          inLanguage: lang === "fr" ? "fr-CA" : "en-CA",
          mainEntity: {
            "@id": `${SITE}/#studio`,
            "@type": "LocalBusiness",
            name: "Phaneroo Prod",
            telephone: "+1-506-871-6122",
            email: "contact@phanerooventures.com",
            contactPoint: [{
              "@type": "ContactPoint",
              contactType: "customer service",
              telephone: "+1-506-871-6122",
              email: "contact@phanerooventures.com",
              areaServed: "CA",
              availableLanguage: ["French", "English"],
            }],
            address: { "@type": "PostalAddress", addressLocality: "Moncton", addressRegion: "NB", postalCode: "E1C 1A1", addressCountry: "CA" },
          },
        }, {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: lang === "fr" ? "Accueil" : "Home", item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: "Contact", item: `${SITE}/contact` },
          ],
        }]}
      />
      <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-ink-300">
                <span className="block w-8 h-px bg-rouge" /><span>{t.contact.eyebrow}</span>
              </div>
              <h1 className="mt-6 font-serif-editorial text-5xl sm:text-6xl lg:text-7xl text-ink-100 tracking-tighter leading-[1.02]">
                {t.contact.title}
              </h1>
              <p className="mt-6 text-ink-300 text-base lg:text-lg max-w-md">{t.contact.sub}</p>
            </Reveal>
            <div className="mt-14 space-y-6 border-t border-hair pt-8">
              <a href="mailto:contact@phanerooventures.com" className="flex items-start gap-4 group" data-testid="contact-email-link">
                <Mail size={18} className="text-rouge mt-1" />
                <div>
                  <div className="text-[10px] tracking-[0.24em] uppercase text-ink-500">Email</div>
                  <div className="text-ink-100 text-lg group-hover:text-rouge transition-colors">contact@phanerooventures.com</div>
                </div>
              </a>
              <a href="tel:+15068716122" className="flex items-start gap-4 group" data-testid="contact-phone-link">
                <Phone size={18} className="text-rouge mt-1" />
                <div>
                  <div className="text-[10px] tracking-[0.24em] uppercase text-ink-500">{lang === "fr" ? "Téléphone" : "Phone"}</div>
                  <div className="text-ink-100 text-lg group-hover:text-rouge transition-colors">+1 (506) 871-6122</div>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <MapPin size={18} className="text-rouge mt-1" />
                <div>
                  <div className="text-[10px] tracking-[0.24em] uppercase text-ink-500">{lang === "fr" ? "Studio" : "Studio"}</div>
                  <div className="text-ink-100 text-lg">Moncton, NB · Canada</div>
                </div>
              </div>
            </div>
          </div>

          <Reveal className="lg:col-span-7" delay={0.1}>
            <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" data-testid="contact-form">
              <div className="md:col-span-2"><input data-testid="form-name" required placeholder={t.contact.name} value={form.name} onChange={set("name")} className="editorial-field" /></div>
              <div><input data-testid="form-email" required type="email" placeholder={t.contact.email} value={form.email} onChange={set("email")} className="editorial-field" /></div>
              <div><input data-testid="form-phone" placeholder={t.contact.phone} value={form.phone} onChange={set("phone")} className="editorial-field" /></div>
              <div><input data-testid="form-company" placeholder={t.contact.company} value={form.company} onChange={set("company")} className="editorial-field" /></div>
              <div className="relative">
                <label className="text-[10px] tracking-[0.24em] uppercase text-ink-500">{t.contact.target_date}</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="form-target-date"
                      className="editorial-field flex items-center justify-between text-left w-full"
                    >
                      <span className={form.target_date ? "text-ink-100" : "text-ink-500"}>
                        {form.target_date
                          ? new Date(form.target_date).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { day: "2-digit", month: "long", year: "numeric" })
                          : (lang === "fr" ? "Choisir une date" : "Pick a date")}
                      </span>
                      <CalendarIcon size={14} className="text-rouge" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="w-auto p-0 bg-[#0c0c0e] border border-hair text-ink-100"
                    data-testid="form-target-date-popover"
                  >
                    <Calendar
                      mode="single"
                      selected={form.target_date ? new Date(form.target_date) : undefined}
                      onSelect={(d) => setForm((f) => ({ ...f, target_date: d ? d.toISOString().slice(0, 10) : "" }))}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.24em] uppercase text-ink-500">{t.contact.project_type}</label>
                <select data-testid="form-project-type" value={form.project_type} onChange={set("project_type")} className="editorial-field bg-[#050505]">
                  {t.contact.project_options.map((o) => <option key={o} value={o} className="bg-[#0a0a0a]">{o}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.24em] uppercase text-ink-500">{t.contact.budget}</label>
                <select data-testid="form-budget" value={form.budget} onChange={set("budget")} className="editorial-field bg-[#050505]">
                  {t.contact.budget_options.map((o) => <option key={o} value={o} className="bg-[#0a0a0a]">{o}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <textarea data-testid="form-message" required rows={5} placeholder={t.contact.message} value={form.message} onChange={set("message")} className="editorial-field resize-none" />
              </div>
              <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-2">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={sending}
                  data-testid="form-submit"
                  className="group inline-flex items-center gap-3 bg-rouge hover:bg-rouge-deep disabled:opacity-60 text-ink-100 px-8 py-4 text-[11px] tracking-[0.24em] uppercase transition-colors"
                >
                  {sending ? t.contact.sending : t.contact.submit}
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </motion.button>
                {done && <span className="text-ink-300 text-sm" data-testid="form-success">{t.contact.success}</span>}
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
